import { ChevronDown, FileText } from "lucide-react";
import React, { useState } from "react";

import { JobDetails } from "../utils/helpers";

interface JobDescriptionProps {
	jobDetails: JobDetails;
}

export function JobDescription({ jobDetails }: JobDescriptionProps) {
	// Collapsed state for mobile - default to collapsed on mobile
	const [isJDExpanded, setIsJDExpanded] = useState(false);

	// Check if we're on mobile (this is a simple check, could be enhanced with a hook)
	const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

	return (
		<div className="mb-8">
			{/* Section Header */}
			<div
				className="flex items-center justify-between cursor-pointer md:cursor-default py-3 border-b border-gray-200"
				onClick={() => isMobile && setIsJDExpanded(!isJDExpanded)}
			>
				<div className="flex items-center gap-2">
					<FileText className="h-5 w-5 text-gray-500" />
					<div>
						<h2 className="text-base font-semibold font-inter text-gray-900">Official job description</h2>
						<p className="text-xs text-gray-500 hidden sm:block">
							This content is provided by the employer and shown exactly as posted.
						</p>
					</div>
				</div>

				{/* Mobile expand/collapse button */}
				<button
					className="md:hidden p-1 rounded-full hover:bg-gray-100 transition-colors"
					aria-expanded={isJDExpanded}
					aria-label={isJDExpanded ? "Collapse job description" : "Expand job description"}
				>
					<ChevronDown
						className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isJDExpanded ? "rotate-180" : ""}`}
					/>
				</button>
			</div>

			{/* Mobile hint */}
			<p className="text-xs text-gray-500 mt-2 sm:hidden">
				{isJDExpanded ? "This is the employer's original posting." : "Tap to view the employer's original posting."}
			</p>

			{/* Job Description Content - Always visible on desktop, collapsible on mobile */}
			<div
				className={`mt-4 space-y-6 ${
					isMobile && !isJDExpanded ? "hidden" : "block md:block" // Always show on desktop
				}`}
			>
				{/* Job Description */}
				{jobDetails?.jobDescription?.lookingToHire && (
					<div>
						<h3 className="text-sm font-semibold mb-2 font-inter text-gray-800">About the Role</h3>
						<div
							className="text-gray-700 font-inter text-sm"
							dangerouslySetInnerHTML={{
								__html: jobDetails.jobDescription.lookingToHire,
							}}
						/>
					</div>
				)}

				{/* Responsibilities */}
				{jobDetails?.jobDescription?.responsibilities && jobDetails.jobDescription.responsibilities.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold mb-2 font-inter text-gray-800">Responsibilities</h3>
						<ul className="list-disc text-gray-700 font-inter pl-4 text-sm space-y-1">
							{jobDetails.jobDescription.responsibilities.map((item, index) => (
								<li key={index}>
									<div
										dangerouslySetInnerHTML={{
											__html: item.value,
										}}
									/>
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Requirements */}
				{jobDetails?.jobDescription?.qualifications && jobDetails.jobDescription.qualifications.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold mb-2 font-inter text-gray-800">Requirements</h3>
						<ul className="list-disc text-gray-700 font-inter pl-4 text-sm space-y-1">
							{jobDetails.jobDescription.qualifications.map((item, index) => (
								<li key={index}>
									<div
										dangerouslySetInnerHTML={{
											__html: item.value,
										}}
									/>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>

			{/* Mobile expand CTA when collapsed */}
			{isMobile && !isJDExpanded && (
				<button
					onClick={() => setIsJDExpanded(true)}
					className="mt-3 w-full py-2 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-600 font-medium transition-colors flex items-center justify-center gap-2"
				>
					<span>View full job description</span>
					<ChevronDown className="h-4 w-4" />
				</button>
			)}
		</div>
	);
}
